import { SubAccountPermissionSection } from 'vopay';
import { useCallback, useRef, useState } from 'react';
import { api } from '@/services/api.config';

/**
 * The payment-rail toggles of one partner account this partner created (ID-13788 item 5), in the
 * account permissions' nested tree — read as the parent, by the partner account's ID.
 */
export default function useFetchPartnerPartnerPermissions() {
  const [fetchingPartnerPermissions, setFetchingPartnerPermissions] = useState(false);
  const [partnerPermissions, setPartnerPermissions] = useState<SubAccountPermissionSection[]>();

  /**
   * Only the newest request may write to state. The drawer refetches as it opens for one partner
   * after another, and nothing cancels the request already in flight — so without this an earlier
   * response landing last would show one partner's rails under another's name.
   */
  const latestRequestId = useRef(0);

  const fetchPartnerPartnerPermissions = useCallback(async (partnerAccountID: string) => {
    latestRequestId.current += 1;
    const requestId = latestRequestId.current;
    const isLatest = () => latestRequestId.current === requestId;

    try {
      setFetchingPartnerPermissions(true);
      const res = await api.fetchPartnerPartnerPermissions({ PartnerAccountID: partnerAccountID });
      if (!res.Success) throw new Error(res.ErrorMessage);
      if (isLatest()) setPartnerPermissions(res.PermissionValues);

      return res;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      if (isLatest()) setFetchingPartnerPermissions(false);
    }
  }, []);

  const reset = useCallback(() => {
    // A reset supersedes whatever is in flight, so that response can no longer land on the cleared state.
    latestRequestId.current += 1;
    setPartnerPermissions(undefined);
    setFetchingPartnerPermissions(false);
  }, []);

  return { fetchingPartnerPermissions, partnerPermissions, fetchPartnerPartnerPermissions, reset };
}
